import type { BaseWidgetProps, WidgetRegistration } from "@/types"
import type { ComponentType } from "react"
import { z } from "zod"

/**
 * WidgetRegistry provides a type-safe registry pattern for widget components.
 * Each widget type is registered with its component and Zod validation schema.
 */
class WidgetRegistry {
  private registry = new Map<string, WidgetRegistration>()

  // TODO: this data prop constraint is maybe too strict?
  /**
   * Register a widget component with its validation schema
   */
  register<T extends BaseWidgetProps>(
    type: string,
    component: ComponentType<T>,
    schema: z.ZodType,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const registration: any = { component, schema }
    this.registry.set(type, registration)
  }

  /**
   * Get a registered widget component
   */
  get(type: string): WidgetRegistration | undefined {
    return this.registry.get(type)
  }

  /**
   * Check if a widget type is registered
   */
  has(type: string): boolean {
    return this.registry.has(type)
  }

  /**
   * Validate widget data against its registered schema
   */
  validate(
    type: string,
    data: unknown,
  ): { success: boolean; data?: unknown; error?: z.ZodError } {
    const registration = this.registry.get(type)
    if (!registration) {
      return {
        success: false,
        error: new z.ZodError([
          {
            code: "custom",
            path: ["type"],
            message: `Unknown widget type: ${type}`,
          },
        ]),
      }
    }

    const result = registration.schema.safeParse(data)
    if (result.success) {
      return { success: true, data: result.data }
    }
    return { success: false, error: result.error }
  }

  /**
   * Get all registered widget types
   */
  getTypes(): string[] {
    return Array.from(this.registry.keys())
  }

  /**
   * Unregister a widget type
   */
  unregister(type: string): boolean {
    return this.registry.delete(type)
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.registry.clear()
  }
}

// Export singleton instance
export const widgetRegistry = new WidgetRegistry()

// Export class for testing
export { WidgetRegistry }
